import type { PaginationDto, PaginationResponseDto } from "core/dto";
import { LOCALE, ORDER_BY } from "core/interfaces";
import type { PageMetaDto } from "shared/dto";

import { getColorDefault } from "~/common/defaults/color.default";
import { getPaginationDefault } from "~/common/defaults/pagination.default";
import { getPaginationResponseDefault } from "~/common/defaults/pagination-response.default";
import { colorNormalize } from "~/common/helpers/color-adapter";
import type { CreateColorDto } from "~/common/interfaces/dto/colors/create-color.dto";
import type { GetAllColorsDto } from "~/common/interfaces/dto/colors/get-all-colors.dto";
import type { UpdateColorByIdDto } from "~/common/interfaces/dto/colors/update-color-by-id.dto";
import type { IdDto } from "~/common/interfaces/dto/id.dto";
import type { Color, ColorAdapted } from "~/common/interfaces/entities/color.interface";
import type { Sort } from "~/common/interfaces/sort.interface";

export const useColorsStore = defineStore(
	"colors",
	() => {
		const { locale } = useI18n();
		const colorNormalizeWithLocale = colorNormalize.bind(this, locale as Ref<LOCALE>);

		const currentColor = ref<ColorAdapted>(getColorDefault());
		const colors = ref<ColorAdapted[]>([]);
		const meta = ref<PageMetaDto>(getPaginationResponseDefault());
		const recentGetAllQuery = ref<GetAllColorsDto & PaginationDto>();
		const tableSort = ref<Sort>({
			columns: [],
			orderBy: ["createdAt", ORDER_BY.DESC]
		});

		const createOne = async (dto: CreateColorDto): Promise<ColorAdapted> => {
			const color = await useNuxtApp().$apiFetch<Color>("/colors", {
				method: "POST",
				body: dto
			});

			return colorNormalizeWithLocale(color);
		};
		const getAll = async (
			dto: GetAllColorsDto = {},
			paginationDto?: Partial<PaginationDto>
		): Promise<PaginationResponseDto<ColorAdapted>> => {
			const [column, orderBy] = tableSort.value.orderBy;

			recentGetAllQuery.value = {
				...getPaginationDefault(),
				...paginationDto,
				...dto,
				column,
				orderBy
			};

			const res = await useNuxtApp().$apiFetch<PaginationResponseDto<Color>>(
				"/colors",
				{
					query: recentGetAllQuery.value
				}
			);
			colors.value = res.data.map(color => colorNormalizeWithLocale(color));
			meta.value = res.meta;

			return res as PaginationResponseDto<ColorAdapted>;
		};
		const getById = async (id: number): Promise<ColorAdapted> => {
			const color = await useNuxtApp().$apiFetch<Color>(`/colors/${id}`);

			return colorNormalizeWithLocale(color);
		};
		const patchById = async (id: number, dto: UpdateColorByIdDto): Promise<ColorAdapted> => {
			const color = await useNuxtApp().$apiFetch<Color>(`/colors/${id}`, {
				method: "PATCH",
				body: dto
			});

			return colorNormalizeWithLocale(color);
		};
		const deleteById = async (id: number): Promise<IdDto> => {
			const res = await useNuxtApp().$apiFetch<IdDto>(`/colors/${id}`, {
				method: "DELETE"
			});

			return res;
		};

		return {
			currentColor,
			colors,
			meta,
			recentGetAllQuery,
			tableSort,

			createOne,
			getAll,
			getById,
			patchById,
			deleteById
		};
	},
	{
		persist: {
			paths: ["tableSort.orderBy"]
		}
	}
);
